import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../../buttons/IconButton';

const minShareOf = (filters) =>
  Number.isFinite(filters.percentageFilter) ? filters.percentageFilter : 0;

// A mouse click or a tap has a click count (detail 1 or more). Enter arrives as a keydown, Space
// as a keyup, a screen reader's click has detail 0: only those hand the focus on to another chip.
const isPointerClick = (event) => event?.type === 'click' && event.detail > 0;

/**
 * One rule per removable chip, in chip order. `key` is the appliedGraphFilters key the chip's X
 * lifts (useShareholderState.removeAppliedFilters); a rule is active while its filter hides
 * something.
 */
const CHIP_RULES = [
  {
    key: 'percentageFilter',
    label: 'Min. share',
    isActive: (filters) => minShareOf(filters) > 0,
    valueText: (filters) => `${minShareOf(filters)}%`
  },
  {
    key: 'expandGeneralPartners',
    label: 'Owners of general partners',
    isActive: (filters, context) =>
      !filters.expandGeneralPartners && (context.collapsedGeneralPartnerCount ?? 0) > 0,
    valueText: () => 'hidden',
    removeLabel: 'Show owners of general partners'
  },
  {
    key: 'includeUnknownShares',
    label: 'Unknown values',
    isActive: (filters) => !filters.includeUnknownShares,
    valueText: () => 'excluded'
  },
  {
    key: 'includeSubsidiaries',
    label: 'Subsidiaries',
    isActive: (filters) => !filters.includeSubsidiaries,
    valueText: () => 'excluded'
  }
];

/**
 * The chips of the applied graph filters that currently hide something, e.g.
 * { key: 'percentageFilter', label: 'Min. share', valueText: '5%', removeLabel: 'Remove Min. share filter' }.
 *
 * @param {Object} [filters] - the applied filters (useShareholderState.appliedGraphFilters)
 * @param {{collapsedGeneralPartnerCount?: number}} [context] - from the prepared graph
 * @returns {Array<{key: string, label: string, valueText: string, removeLabel: string}>}
 */
export const buildFilterChips = (filters = {}, context = {}) =>
  CHIP_RULES.filter((rule) => rule.isActive(filters, context)).map((rule) => ({
    key: rule.key,
    label: rule.label,
    valueText: rule.valueText(filters),
    removeLabel: rule.removeLabel ?? `Remove ${rule.label} filter`
  }));

/**
 * ShareholderFilterChips - the search results' removable chips next to the Filter button. Only
 * applied filters that hide something get a chip; its X lifts that filter. After a removal from
 * the keyboard (or a screen reader) the focus moves to the next chip's X, else the previous one,
 * else the element `fallbackFocusId` names (the Filter button). A mouse or touch removal moves
 * no focus, like the search results' chips, so no other chip's tooltip opens on focus. Renders
 * nothing when no filter hides anything.
 *
 * @param {Object} props
 * @param {{percentageFilter: number, includeUnknownShares: boolean, includeSubsidiaries: boolean,
 *   expandGeneralPartners: boolean}} props.filters - the applied filters
 *   (useShareholderState.appliedGraphFilters)
 * @param {{collapsedGeneralPartnerCount?: number}} [props.context] - from the prepared graph
 * @param {Function} props.onRemoveFilters - (filterKeys) => lift those filters
 * @param {string} [props.fallbackFocusId] - focused when the last chip was removed
 */
const ShareholderFilterChips = ({ filters, context = {}, onRemoveFilters, fallbackFocusId }) => {
  const listRef = useRef(null);
  const pendingFocusRef = useRef(null);
  const chips = buildFilterChips(filters, context);
  const chipKeys = chips.map((chip) => chip.key).join(',');

  useEffect(() => {
    const index = pendingFocusRef.current;
    if (index == null) {
      return;
    }
    pendingFocusRef.current = null;
    const buttons = listRef.current?.querySelectorAll('[role="button"]') ?? [];
    const fallback = fallbackFocusId ? document.getElementById(fallbackFocusId) : null;
    const next = buttons.length ? buttons[Math.min(index, buttons.length - 1)] : fallback;
    next?.focus();
  }, [chipKeys, fallbackFocusId]);

  if (!chips.length) {
    return null;
  }

  return (
    <fieldset className="shareholder-filter-chips unified-chip-rail" aria-label="Applied filters">
      <div className="filters-chips-list-main-wrap">
        <div className="filters-chips-list" ref={listRef}>
          {chips.map((chip, index) => (
            <div className="filters-chips-item unified-chip noselect" key={chip.key}>
              <div className="display-text">
                <span className="unified-chip-key">{chip.label}:</span>
                <span className="unified-chip-value">{chip.valueText}</span>
              </div>
              <IconButton
                accessible
                ariaLabel={chip.removeLabel}
                tooltipText={chip.removeLabel}
                icon={ICON_BUTTON_ICONS.X_LG}
                type={ICON_BUTTON_TYPES.BLUE}
                withFill={false}
                withBorder={false}
                onClick={(event) => {
                  pendingFocusRef.current = isPointerClick(event) ? null : index;
                  onRemoveFilters([chip.key]);
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </fieldset>
  );
};

ShareholderFilterChips.propTypes = {
  filters: PropTypes.shape({
    percentageFilter: PropTypes.number,
    includeUnknownShares: PropTypes.bool,
    includeSubsidiaries: PropTypes.bool,
    expandGeneralPartners: PropTypes.bool
  }).isRequired,
  context: PropTypes.shape({ collapsedGeneralPartnerCount: PropTypes.number }),
  onRemoveFilters: PropTypes.func.isRequired,
  fallbackFocusId: PropTypes.string
};

export default ShareholderFilterChips;
